/**
 * WeightProjection.tsx — the "what staying with this could achieve" band that
 * opens the plan builder.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY (2026-08-17, business): the plan builder opened on a price. A patient
 * deciding between three and twelve months has nothing to weigh that price
 * against, so the longer term reads only as "more money for longer" rather than
 * "more time for the treatment to work". This band puts the outcome beside the
 * commitment at the moment the choice is made.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠️  EVERY NUMBER HERE COMES OFF THE WIRE. NONE OF IT MAY BE DERIVED.
 *
 * Each figure is a CLINICAL EFFICACY CLAIM about a prescription-only medicine,
 * made to an identifiable patient we have just assessed. The design mock drew it
 * as a curve, and a curve is trivial to generate in the browser — which is
 * exactly the trap. A projection computed client-side would be unsubstantiated
 * (no trial basis, no clinical owner, no audit trail), uncorrectable without a
 * portal release, and — to the patient — indistinguishable from a real one.
 *
 * So `points` is `PlanOption.projection`, seeded as rows in prices-service and
 * owned there. This component plots what it is given and NOTHING ELSE:
 *
 *   - it returns `null` below two points, so a half-seeded catalogue draws no
 *     slope rather than a fabricated one;
 *   - it never interpolates. A month between two supplied points is not drawn as
 *     a value, and `PlanOption.expectedWeightLossPercent` is never spread across
 *     the terms to manufacture a curve;
 *   - the only arithmetic it performs on a percentage is multiplying it by the
 *     patient's own recorded starting weight, which is a unit conversion, not a
 *     new claim. No starting weight ⇒ percentages only, never a kilogram figure
 *     invented from an assumed average body weight.
 *
 * An ABSENT chart is recoverable. A plausible invented one, in front of someone
 * deciding whether to start a prescription medicine, is not.
 *
 * ⚠️ THE AXIS IS A SCALE, NOT DATA (2026-08-18, Ashley Haveloch-Jones). The
 * stakeholder asked for "the chart month 1-12" with "the left axis be % of
 * weight loss". Both are honoured on the AXIS ONLY: the x domain spans the first
 * projected month to the last, so a 1→12 catalogue uses the full width instead
 * of bunching everything into the left tenth, and the y gutter is labelled in
 * whole percent. Neither invents a data point. The months BETWEEN the supplied
 * points stay empty: no figure is printed, listed or labelled for them, because
 * a value at month 4 is a claim nobody signed off.
 *
 * ⚠️ A SMOOTH LINE, AND WHY THAT IS STILL "NO INTERPOLATION" (2026-09-26, CTO:
 * "this chart looks quite hard edges and looks quite broken"). Until then the
 * line ran STRAIGHT from one stated figure to the next, which is a picture of
 * linear interpolation — every straight segment passes through a "value" at
 * month 4 just as a curve does. So the question was never straight versus
 * curved; it is whether the line can say MORE than the points. The line is now a
 * MONOTONE cubic through exactly the server's points (`monotoneTangents`,
 * Fritsch–Carlson): between two points it never rises above the higher or dips
 * below the lower, so it cannot draw a bulge, a dip or a faster month the data
 * does not have. Each supplied month is marked with its own dot, so the reader
 * can see which positions are figures and which are only the line between them.
 * The figures a patient READS — the headline, the sr-only list — are still the
 * server's, at the server's months, and nothing else.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * R28: no medication name, brand or delivery form appears here. The band talks
 * about the TERM and the outcome; the treatment is named by the cards below,
 * from server data.
 *
 * Accessibility: the chart is decorative REINFORCEMENT, never the message. The
 * headline figure and the sentence beside it carry the whole meaning in text, and
 * the full curve is additionally restated as a plain list for screen readers — so
 * nothing here depends on seeing a shape or distinguishing two line weights
 * (WCAG 1.4.1). The SVG itself is `aria-hidden`, and so are BOTH label gutters:
 * they are tick marks for the picture, and reading "0% 25% 1m 12m" aloud between
 * the headline and the sr-only list would bury the two things that actually carry
 * the meaning.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE MOUNT REVEAL (2026-08-20, CTO: "the graph at plan builder is not as good
 * as the graph we have at the beginning of the questionnaire… it's nicely
 * animated, perfect size").
 *
 * The chart he means is {@link ../journey/ProjectionChart}, and what this band
 * borrows from it is the MOTION and the LOOK: both curves draw themselves on
 * mount (SVG `stroke-dashoffset`, CSS-transitioned) while the shaded wedge and
 * the endpoint dot fade in behind them. Both charts gate that on the ONE shared
 * `usePrefersReducedMotion` in {@link ../ui/motion} — a second reduced-motion
 * check here would drift from the first, and the cohort it drifts away from is
 * the one that asked not to be moved. `useRevealAfterMount` is the same shared
 * "paint the hidden state, flip a frame later so the transition actually plays"
 * helper the rest of the polish uses. Reduced-motion users, and any environment
 * with no `matchMedia` (a non-browser test runner, a server render), get the
 * FINISHED chart on the very first paint — never a chart that paints blank and
 * snaps in.
 *
 * ⚠️ THE ONE THING THIS BAND MAY NOT BORROW IS THE OTHER CHART'S DATA MODEL, and
 * the next person asked to "make them consistent" will reach for exactly that.
 * `ProjectionChart` COMPUTES its curve in the browser from a loss factor
 * (`journey/projection.ts`): it is category-level, pre-eligibility and
 * illustrative, and nobody is named in it. Every figure HERE is a clinical
 * efficacy claim about a prescription-only medicine, made to a patient we have
 * just assessed — so the rules at the top of this file stand exactly as they
 * are, unchanged by anything below: never interpolate a month the server did not
 * send, never spread `expectedWeightLossPercent` across the terms, render
 * nothing below two points, and derive kilograms only by multiplying a server
 * percentage by the patient's own recorded starting weight. The animation moves
 * a line that is already there. It must never be the thing that draws one.
 *
 * The shared drawing bits are NOT extracted into a common module, and that is a
 * deliberate limit rather than an oversight: the two charts plot different
 * shapes from different sources, and the only genuinely common pieces — the
 * reduced-motion gate and the mount-reveal flip — are ALREADY shared, in
 * `ui/motion`. What is left to "share" is a polyline length and a dash pair,
 * which is less code than the seam that would hold them.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useRef, useState } from 'react';
import { formatKg } from '../lib/format';
import { usePrefersReducedMotion, useRevealAfterMount } from '../ui/motion';

/** One term's projected outcome, exactly as the server states it. */
export interface ProjectionPoint {
  months: number;
  /** Whole percent of body weight, server-supplied. Never derived here. */
  percent: number;
}

/**
 * The shortest run that can honestly be called a projection. One point is a
 * single claim with nothing to compare it against, and drawing a line through
 * it would imply a trajectory the server never stated.
 */
const MIN_POINTS = 2;

/**
 * Chart box.
 *
 * W IS A UNIT SYSTEM, NOT A SIZE. `preserveAspectRatio="none"` stretches the
 * viewBox to whatever width the card hands it, so 320 is only the x domain the
 * path arithmetic is written in. HEIGHT IS NOT LIKE THAT: the SVG's CSS pixel
 * height is pinned to its viewBox height, 1:1, because the percentage ticks sit
 * in an HTML gutter positioned with {@link yFor} — which returns viewBox units.
 * Let the two coordinate systems diverge by a single pixel and every printed
 * tick drifts off the gridline it names. Whatever else changes here, those two
 * numbers stay the same number.
 *
 * TWO HEIGHTS, NOT ONE (2026-08-19, CTO: "still the chart looks so squashed" —
 * the same complaint Ashley Haveloch-Jones made on 2026-08-18, which the last
 * pass answered by widening the plot but left short).
 *
 * The band renders inside `.gps-plan-breakout` — `min(64rem, 94vw)` — and, from
 * 880px, inside that band's LEFT COLUMN, because the order summary took a rail
 * off the right (2026-08-20). ONE number cannot serve the range that leaves: at
 * the old 150px the plot was a reasonable 1.8:1 on a phone and a 6.3:1
 * letterbox on a desktop, and inside a 6.3:1 letterbox a descent from 2% to 21%
 * reads as very nearly flat. The shape of that curve IS the argument for the
 * longer term, so a curve flattened by its own frame argues the opposite of what
 * this band exists to say.
 *
 * Measured in a browser at four widths, not guessed — and note the plot is no
 * longer widest on the widest screen, because the rail appears at 880px and the
 * band stops growing at 64rem:
 *
 *   375px phone   272 x 180  = 1.5:1   (one column, NARROW box)
 *   768px tablet  642 x 300  = 2.1:1   (still one column, WIDE box)
 *   880px         395 x 300  = 1.3:1   (the rail arrives — the tightest case)
 *   1280px        528 x 300  = 1.8:1
 *
 * Every one of those is inside the range a line chart is actually read at, and
 * none of them is a letterbox — which is the failure this pair exists to
 * prevent. Re-measure if the rail's width changes.
 *
 * ⚠️ Neither number may be derived from the data. The height is a frame; the
 * y-scale inside it still clears the largest SERVER-supplied figure (see
 * {@link axisCeiling}), so a taller box never rescales a claim.
 */
/**
 * The plot is drawn at its REAL width (2026-09-26). It used to be a fixed
 * 320-unit viewBox stretched by `preserveAspectRatio="none"` to whatever the
 * card gave it, which scaled the x axis and not the y: on a phone the line thinned
 * wherever it ran sideways, round joins went flat and the endpoint dot became an
 * oval — the "broken" look the CTO flagged. `useChartWidth` measures the box, and
 * the viewBox width IS that width, so one viewBox unit is one pixel both ways and
 * nothing is stretched. 320 is only the width drawn before the first measurement
 * (and in a test runner, which has no layout).
 */
const W_FALLBACK = 320;
const H_NARROW = 180;
const H_WIDE = 300;

/**
 * Where the taller box starts.
 *
 * 768px rather than the 880px `.gps-plan-layout` switches at: that breakpoint
 * answers "do two columns fit", and this one answers "is the plot wide enough
 * to look shallow" — which happens a good deal earlier.
 */
const WIDE_QUERY = '(min-width: 768px)';

function matchesWide(): boolean {
  // jsdom has no matchMedia (and neither would a server render). Both fall to
  // the NARROW box on purpose: it is the height that fits everywhere, so the
  // failure mode of not knowing the viewport is a chart that is too short on a
  // desktop, never one that pushes the choices off a phone screen.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(WIDE_QUERY).matches;
}

/**
 * The plot height for this viewport, tracked live across a resize or a rotate.
 *
 * A media-query LISTENER rather than a CSS media query, because this number is
 * needed in three places at once and CSS can only reach one of them: it is the
 * viewBox height, the SVG's pixel height, and the height the HTML tick gutter
 * is laid out in. Splitting it between a stylesheet and the path arithmetic is
 * precisely how the gutter and the gridlines would come apart.
 */
/** The plot box's width in pixels, tracked live; {@link W_FALLBACK} until measured. */
function useChartWidth(ref: { current: HTMLElement | null }): number {
  const [width, setWidth] = useState(W_FALLBACK);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver !== 'function') return;
    const measure = () => {
      const next = Math.round(node.getBoundingClientRect().width);
      // A box with no width (not laid out yet, or hidden) keeps the last width
      // rather than collapsing the plot to nothing.
      if (next > 0) setWidth((prev) => (prev === next ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

function useChartHeight(): number {
  const [wide, setWide] = useState(matchesWide);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(WIDE_QUERY);
    const onChange = () => setWide(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return wide ? H_WIDE : H_NARROW;
}

const PAD_X = 8;
/**
 * The 0% line is the TOP of the plot — the patient's starting weight — and the
 * shaded wedge closes to it. Keep it small: it is also the y a spec pins to
 * prove the wedge opens downward from the starting weight rather than upward
 * from the floor.
 */
const PAD_TOP = 8;
const PAD_BOTTOM = 8;

interface PlottedPoint extends ProjectionPoint {
  x: number;
  y: number;
}

/**
 * The top of the percentage scale.
 *
 * Rounded UP to the next whole 5% STRICTLY above the largest projected figure,
 * for two reasons. A scale topped exactly at the data maximum puts the final
 * point on the frame, where the endpoint dot is clipped and the curve looks like
 * it ran out of chart rather than out of months. And a scale that moved with
 * every catalogue edit would silently rescale the same curve — two treatments
 * whose figures differ by a point would draw identical-looking slopes, which is
 * precisely the comparison the band exists to support.
 */
function axisCeiling(maxPercent: number): number {
  const step = 5;
  return Math.max(step, Math.floor(maxPercent / step) * step + step);
}

/**
 * Which percentages get a printed label. The ends always, plus the midpoint when
 * it lands on a whole number — a scale reading "0% / 12.5% / 25%" prints a
 * precision the seeded figures do not have, and three labels are not worth that.
 */
function axisTicks(ceiling: number): number[] {
  const mid = ceiling / 2;
  return Number.isInteger(mid) ? [0, mid, ceiling] : [0, ceiling];
}

/**
 * Place each supplied point in the box.
 *
 * The y axis runs DOWNWARD with loss — more weight lost sits lower on the chart —
 * because that is the direction the patient's weight is going, and an ascending
 * line would read as gaining. y is scaled against the ROUNDED ceiling, not the
 * data maximum, so the printed tick labels and the plotted points share one
 * scale (see {@link axisCeiling}).
 *
 * x is spaced by MONTH and anchored to the FIRST projected month rather than to
 * zero. Anchoring at zero cost the plot a whole month of width for a curve that
 * starts at month 1 and, worse, implied a point at the origin that the server
 * never sent. Spacing by month rather than by index is what keeps the shape
 * honest: the supplied months are 1/2/3/6/12, so even spacing would make the
 * last nine months look like the first three and flatten the early ramp.
 *
 * `height` is a PARAMETER rather than the module constant it used to be,
 * because the box is now viewport-dependent (see {@link useChartHeight}). It is
 * the one input here that is not the server's data — and it only ever moves the
 * frame, never a figure inside it.
 */
function plot(points: ProjectionPoint[], ceiling: number, height: number, width: number): PlottedPoint[] {
  const first = points[0].months;
  const span = points[points.length - 1].months - first;
  const innerW = width - PAD_X * 2;
  const innerH = height - PAD_TOP - PAD_BOTTOM;
  return points.map((p) => ({
    ...p,
    x: PAD_X + innerW * ((p.months - first) / span),
    y: PAD_TOP + innerH * (p.percent / ceiling),
  }));
}

/**
 * The y a percentage sits at — shared by the plot and the printed tick labels.
 *
 * SHARED IS THE POINT: the gutter labels and the gridlines are two renderings
 * of one scale, so they must be computed by one function taking one height. A
 * second copy of this arithmetic is how a "25%" label ends up beside a line
 * that is not the 25% line.
 */
function yFor(percent: number, ceiling: number, height: number): number {
  return PAD_TOP + (height - PAD_TOP - PAD_BOTTOM) * (percent / ceiling);
}

/**
 * How close two month labels may sit, as a fraction of the plot width.
 *
 * The labels are positioned by month, so on a 5-point curve (1/2/3/6/12) the
 * first two land ~9% apart — which is under 15px on a phone, and "1m2m" runs
 * together. Rather than guess at a breakpoint, keep the three labels that carry
 * meaning (where the curve starts, where it ends, and the term being bought) and
 * drop any that would crowd them.
 */
const MIN_LABEL_GAP = 0.14;

/**
 * Which months get a visible label. The SELECTED term always wins — it is the
 * one the figure above the chart refers to — then the two ends, each dropped if
 * it would collide with it. Every month is still named in full in the sr-only
 * list below, so nothing is lost, only decluttered.
 */
function labelledPoints(pts: PlottedPoint[], months: number, width: number): PlottedPoint[] {
  if (pts.length === 0) return [];
  const selected = pts.find((p) => p.months === months) ?? pts[pts.length - 1];
  const first = pts[0];
  const last = pts[pts.length - 1];
  const farEnough = (p: PlottedPoint) => Math.abs(p.x - selected.x) / width >= MIN_LABEL_GAP;
  const keep = [selected];
  if (first !== selected && farEnough(first)) keep.unshift(first);
  if (last !== selected && farEnough(last)) keep.push(last);
  return keep.sort((a, b) => a.months - b.months);
}

/**
 * The slope the line leaves each point at — a MONOTONE cubic (Fritsch–Carlson,
 * the same rule as d3's `curveMonotoneX`). Computed ONCE over the whole curve,
 * so the chosen-term run is drawn along exactly the same line as the faint full
 * curve rather than a second, slightly different one.
 *
 * Monotone is the whole point (see the header): between two points the line
 * never goes above the higher or below the lower, so it cannot show a bump, a
 * dip or a steeper month than the server's figures do. A flat step stays flat.
 */
function monotoneTangents(pts: PlottedPoint[]): number[] {
  const n = pts.length;
  const secant: number[] = [];
  for (let i = 0; i < n - 1; i++) secant.push((pts[i + 1].y - pts[i].y) / (pts[i + 1].x - pts[i].x));
  const t = pts.map((_, i) => {
    if (i === 0) return secant[0];
    if (i === n - 1) return secant[n - 2];
    // A change of direction (or a flat side) gets a flat tangent: no overshoot.
    return secant[i - 1] * secant[i] <= 0 ? 0 : (secant[i - 1] + secant[i]) / 2;
  });
  for (let i = 0; i < n - 1; i++) {
    if (secant[i] === 0) {
      t[i] = 0;
      t[i + 1] = 0;
      continue;
    }
    const a = t[i] / secant[i];
    const b = t[i + 1] / secant[i];
    const sum = a * a + b * b;
    if (sum > 9) {
      const tau = 3 / Math.sqrt(sum);
      t[i] = tau * a * secant[i];
      t[i + 1] = tau * b * secant[i];
    }
  }
  return t;
}

/** The cubic Bézier for segment i → i+1, as its four control points. */
function segment(pts: PlottedPoint[], tangents: number[], i: number): [number, number][] {
  const a = pts[i];
  const b = pts[i + 1];
  const third = (b.x - a.x) / 3;
  return [
    [a.x, a.y],
    [a.x + third, a.y + tangents[i] * third],
    [b.x - third, b.y - tangents[i + 1] * third],
    [b.x, b.y],
  ];
}

const fmt = (n: number) => n.toFixed(1);

/**
 * The line through the first `count` points of the curve (all of them by
 * default): an `M` to the first point, then one `C` per segment. Every segment
 * ENDS on a supplied point, so the curve passes through each figure exactly.
 */
function path(pts: PlottedPoint[], tangents: number[], count = pts.length): string {
  let d = `M${fmt(pts[0].x)} ${fmt(pts[0].y)}`;
  for (let i = 0; i < count - 1; i++) {
    const [, c1, c2, end] = segment(pts, tangents, i);
    d += ` C${fmt(c1[0])} ${fmt(c1[1])} ${fmt(c2[0])} ${fmt(c2[1])} ${fmt(end[0])} ${fmt(end[1])}`;
  }
  return d;
}

/**
 * How long the curve takes to draw itself on mount.
 *
 * The same 1300ms {@link ../journey/ProjectionChart} uses, because the CTO is
 * comparing the two charts directly and a faster or slower draw is exactly the
 * kind of difference that reads as "still not the same". Not `--gps-dur`
 * (200ms): that token is for a control changing state under the reader's finger,
 * and a line drawing itself across a whole card at that speed is a flicker
 * rather than a reveal.
 */
const REVEAL_MS = 1300;

/**
 * The length of the plotted curve, in viewBox units.
 *
 * ⚠️ IT MUST NOT COME OUT SHORT. It becomes the `stroke-dasharray` AND the
 * starting `stroke-dashoffset`, so a dash shorter than the path leaves a
 * permanent gap at the end of a curve whose final point is the twelve-month
 * figure — the chart would quietly stop drawing before its most important
 * claim. The path is cubic, so its length is measured (see below) and padded
 * UP: `path()` prints coordinates to one decimal, and a hair too long merely
 * finishes the draw a frame early, while a hair too short is visible forever.
 *
 * `getTotalLength()` would be the browser's own answer, and is deliberately not
 * used: it needs a mounted node (so the first paint would have no dash at all,
 * which is the flash this reveal exists to avoid) and it returns 0 in jsdom.
 */
function curveLength(pts: PlottedPoint[], tangents: number[], count = pts.length): number {
  // Measured by walking each Bézier in small chords. A chord sum can only come
  // out SHORT of the true arc, and short is the one direction this may never
  // err in (above) — so it is padded by 2% and a unit, then rounded up.
  // Over-long only finishes the draw a frame early.
  const STEPS = 32;
  let total = 0;
  for (let i = 0; i < count - 1; i++) {
    const [p0, p1, p2, p3] = segment(pts, tangents, i);
    let prev = p0;
    for (let k = 1; k <= STEPS; k++) {
      const t = k / STEPS;
      const u = 1 - t;
      const x = u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0];
      const y = u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1];
      total += Math.hypot(x - prev[0], y - prev[1]);
      prev = [x, y];
    }
  }
  return total === 0 ? 0 : Math.ceil(total * 1.02 + 1);
}

/**
 * A stable identity for the CURVE ITSELF, used to restart the reveal.
 *
 * Keyed on the server's figures, NOT on the selected term. Switching treatment
 * swaps the whole projection for a different medicine's, and drawing that in
 * says so; re-drawing the same curve every time the patient tries 3 months
 * against 12 would animate on the one interaction this band exists to support,
 * and a chart that redraws under a term click reads as though the numbers are
 * being recalculated for them — which is the one thing they are not.
 */
function curveKey(points: ProjectionPoint[]): string {
  return points.map((p) => `${p.months}:${p.percent}`).join('|');
}

export function WeightProjection({
  points,
  months,
  startKg,
}: {
  /** Server-supplied, ascending. Fewer than two ⇒ the band is absent. */
  points: ProjectionPoint[];
  months: number;
  /** The patient's recorded starting weight. Absent ⇒ percentages only. */
  startKg?: number;
}) {
  // FIRST STATEMENTS, ABOVE EVERY EARLY RETURN — hooks may not be called
  // conditionally, and this component returns null on four different grounds
  // below. Moving any of them under one breaks the render on the very first
  // catalogue that has no curve.
  const H = useChartHeight();
  const plotBox = useRef<HTMLDivElement | null>(null);
  const width = useChartWidth(plotBox);
  // The ONE reduced-motion signal (ui/motion), shared with the questionnaire's
  // projection chart and the rest of the polish. `reduced` is also true when we
  // cannot ask — no `matchMedia`, i.e. a test runner or a server render — so the
  // finished chart paints first time rather than starting blank.
  const reduced = usePrefersReducedMotion();
  const revealed = useRevealAfterMount(reduced, curveKey(points));

  // FAIL-SILENT, NOT FAIL-LOUD: a missing projection is an ordinary state (the
  // catalogue simply has no figure for this treatment yet), not an error the
  // patient should be shown.
  //
  // ⚠️ NO TERM SELECTOR IS RENDERED HERE, AND NONE MAY EVER BE ADDED. The term
  // control is back above this chart as of 2026-08-19 (CTO) — but it lives in
  // PlanSelection, which is the whole point. Everything below this line is
  // gated on the server having sent a signed-off curve, and a term selector
  // nested in here disappeared with the chart: on a catalogue with no seeded
  // projection the patient could not choose a term at all, and every unit spec
  // stayed green while they could not (fixed 2026-08-17). A pure UI affordance
  // must never be gated on someone else's data. Render it beside this
  // component, never inside it.
  if (points.length < MIN_POINTS) return null;

  const sorted = [...points].sort((a, b) => a.months - b.months);
  const selected = sorted.find((p) => p.months === months);
  // The selected term must be one the server projected. If the patient picks a
  // term with no figure of its own, the band goes quiet rather than showing a
  // neighbour's number as if it were this term's.
  if (!selected) return null;

  // A ZERO-WIDTH TIME AXIS IS MALFORMED DATA, NOT A CHART. Two points on the
  // same month cannot be laid out against elapsed time, and dividing by the span
  // would put every point at the same x with an Infinity in the path. The band
  // goes quiet, exactly as it does for every other shape it cannot honestly draw.
  if (sorted[sorted.length - 1].months === sorted[0].months) return null;

  const ceiling = axisCeiling(Math.max(...sorted.map((p) => p.percent)));
  const pts = plot(sorted, ceiling, H, width);
  const tangents = monotoneTangents(pts);
  // THE WHOLE CURVE IS DRAWN, and the part up to the chosen term is emphasised
  // (2026-08-18, CTO). Showing only the chosen term's segment hid the thing the
  // band is for: a patient weighing 3 against 12 months cannot see what the extra
  // nine months buy if the chart stops where they currently are.
  const upTo = pts.filter((p) => p.months <= months);
  const end = upTo[upTo.length - 1];
  const baseline = H - PAD_BOTTOM;
  // THE SHADED REGION IS THE WEIGHT LOST, so it is closed to the TOP of the plot
  // — the starting weight, which is also the 0% line — not to the bottom axis.
  //
  // Closing it downward (the obvious way to write this, and how it first
  // shipped) fills the area UNDER a line that descends with loss, which makes
  // the green mass largest at month 1 where the least has been lost and shrink
  // as the patient does better. It reads as a solid block on the left and says
  // the opposite of what the chart means. Closed upward the wedge starts at
  // nothing and widens with every month of progress.
  const solidPath = path(pts, tangents, upTo.length);
  const area = `${solidPath} L${end.x.toFixed(1)} ${PAD_TOP} L${upTo[0].x.toFixed(1)} ${PAD_TOP} Z`;

  // ── The mount reveal (see the MOTION note in the file header) ──────────────
  //
  // Each curve is dashed with its OWN length and starts fully offset, so it
  // draws from the first projected month to the last. Both use the plot's own
  // `stroke-linecap: round`, which puts a round cap on the leading edge — the
  // travelling dot the questionnaire's chart runs on a rAF loop is, here, just
  // the tip of the line drawing itself. No animation frame to schedule, nothing
  // to cancel on unmount, and nothing that can strand a dot mid-curve if the
  // patient switches treatment halfway through.
  //
  // ⚠️ NOT A SECOND SOURCE OF TRUTH ABOUT THE DATA. `full`/`solid` are lengths
  // of paths already computed above from the server's figures; the reveal
  // cannot move a point, add one, or change what the chart says once it has
  // finished — it only decides how much of the finished line is painted yet.
  const fullLength = curveLength(pts, tangents);
  const solidLength = curveLength(pts, tangents, upTo.length);
  // A single-point run (the patient is on the FIRST projected term) has zero
  // length, and `stroke-dasharray: 0` hides a stroke entirely rather than
  // drawing it — so the dash is only applied to a run that actually has length.
  const drawStyle = (length: number) =>
    reduced || length === 0
      ? undefined
      : {
          strokeDasharray: length,
          strokeDashoffset: revealed ? 0 : length,
          transition: `stroke-dashoffset ${REVEAL_MS}ms var(--gps-ease)`,
        };
  // The wedge and the endpoint dot fade rather than draw, and land LAST: the
  // dot is where the chosen term's line stops, so showing it before the line
  // has reached it points at a figure the chart has not yet made.
  const fadeStyle = (delayMs: number) => ({
    opacity: revealed ? 1 : 0,
    transition: reduced ? undefined : `opacity ${REVEAL_MS / 2}ms ease-out ${delayMs}ms`,
  });

  const hasWeight = typeof startKg === 'number' && startKg > 0;
  const kgFor = (percent: number) => (startKg as number) * (percent / 100);
  const headline = hasWeight ? formatKg(Math.round(kgFor(selected.percent))) : `${selected.percent}%`;
  const label = (p: ProjectionPoint) =>
    hasWeight ? `${formatKg(Math.round(kgFor(p.percent)))}` : `${p.percent}%`;

  return (
    <section
      // `gps-jux-panel`: the journey's one white card (tokens.css, P7).
      className="gps-jux-panel"
      style={{
        background: 'var(--gps-surface)',
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-lg)',
        padding: 'var(--gps-s-4)',
        marginBottom: 'var(--gps-s-5)',
      }}
    >
      <p
        style={{
          margin: '0 0 10px',
          fontSize: '12px',
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: 'var(--gps-muted)',
        }}
      >
        Your estimated progress
      </p>

      {/* THE HEADLINE SITS ABOVE THE CHART, NOT BESIDE IT (2026-08-18). Sharing
          a flex row with the plot is what made the chart "squashed" on a phone:
          the figure claimed the first 11rem and the curve got whatever was left.
          On its own line it costs one row and hands the plot the full measure. */}
      <p style={{ margin: '0 0 var(--gps-s-4)' }}>
        <span
          style={{
            display: 'block',
            fontSize: '28px',
            fontWeight: 800,
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
            fontFamily: 'var(--gps-font-display)',
          }}
        >
          {headline}
        </span>
        <span style={{ display: 'block', fontSize: '13px', color: 'var(--gps-muted)', marginTop: '3px' }}>
          estimated loss by month {months}
        </span>
      </p>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        {/* THE PERCENTAGE SCALE (2026-08-18, Ashley Haveloch-Jones). HTML, in
            its own gutter, for the same reason the month labels are HTML — see
            the note below. Each label is positioned at the SAME y the plot uses
            for that percentage (`yFor`), against the SAME `H`, so a tick can
            never drift away from the gridline it names — and the SVG's height is
            fixed in pixels to that same `H` as its viewBox height, so the two
            coordinate systems stay 1:1 at both chart sizes. */}
        <div
          aria-hidden="true"
          style={{ position: 'relative', width: '2.4rem', height: `${H}px`, flex: 'none' }}
        >
          {axisTicks(ceiling).map((tick) => (
            <span
              key={tick}
              style={{
                position: 'absolute',
                right: 0,
                top: `${yFor(tick, ceiling, H)}px`,
                transform: 'translateY(-50%)',
                fontSize: '10.5px',
                lineHeight: 1.2,
                color: 'var(--gps-muted)',
                whiteSpace: 'nowrap',
              }}
            >
              {tick}%
            </span>
          ))}
        </div>

        <div ref={plotBox} style={{ flex: '1 1 auto', minWidth: 0 }}>
          <svg
            aria-hidden="true"
            focusable="false"
            viewBox={`0 0 ${width} ${H}`}
            preserveAspectRatio="none"
            style={{ width: '100%', height: `${H}px`, display: 'block' }}
          >
            <defs>
              {/* Densest at the starting-weight line and fading toward the curve,
                  so the wedge reads as depth of loss rather than as a block. */}
              <linearGradient id="gps-proj-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--gps-primary)" stopOpacity="0.20" />
                <stop offset="100%" stopColor="var(--gps-primary)" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            {/* GRIDLINES ARE <line>, NEVER <path>. Two specs count the stroked
                paths to prove the full curve and the chosen-term run are both
                drawn; drawing the scale as paths would make that count meaningless
                and the regression it guards invisible. */}
            {axisTicks(ceiling).map((tick) => (
              <line
                key={tick}
                x1={PAD_X}
                y1={yFor(tick, ceiling, H)}
                x2={width - PAD_X}
                y2={yFor(tick, ceiling, H)}
                stroke="var(--gps-border)"
                strokeWidth="1"
              />
            ))}
            <line x1={PAD_X} y1={baseline} x2={width - PAD_X} y2={baseline} stroke="var(--gps-border)" strokeWidth="1" />
            {/* The full curve, faint: what the later months would add. */}
            <path
              d={path(pts, tangents)}
              fill="none"
              stroke="var(--gps-primary)"
              strokeWidth="2.25"
              strokeOpacity="0.28"
              strokeLinejoin="round"
              strokeLinecap="round"
              style={drawStyle(fullLength)}
            />
            <path d={area} fill="url(#gps-proj-fill)" style={fadeStyle(REVEAL_MS / 3)} />
            {/* The chosen term, solid. */}
            <path
              d={solidPath}
              fill="none"
              stroke="var(--gps-primary)"
              strokeWidth="3"
              strokeLinejoin="round"
              strokeLinecap="round"
              style={drawStyle(solidLength)}
            />
            <circle
              cx={end.x}
              cy={end.y}
              r="5"
              fill="var(--gps-primary)"
              stroke="var(--gps-surface)"
              strokeWidth="2.5"
              style={fadeStyle(REVEAL_MS * 0.75)}
            />
            {/* EVERY SUPPLIED MONTH GETS A DOT (2026-09-26), so the reader can see
                where the figures are and that the line between them is only a
                line. After the endpoint in the DOM so a spec's first circle is
                still the chosen term's; the chosen month itself has no second
                dot (its own is the filled one above). */}
            {pts
              .filter((p) => p.months !== end.months)
              .map((p) => (
                <circle
                  key={p.months}
                  data-point={p.months}
                  cx={p.x}
                  cy={p.y}
                  r="3.25"
                  fill="var(--gps-surface)"
                  stroke="var(--gps-primary)"
                  strokeWidth="1.75"
                  strokeOpacity={p.months < end.months ? 1 : 0.5}
                  style={fadeStyle(REVEAL_MS / 2)}
                />
              ))}
          </svg>

          {/* THE MONTH LABELS ARE HTML, NOT <text> INSIDE THE SVG, and that is not
              a style preference. `preserveAspectRatio="none"` is what lets the
              plot stretch to whatever width the row gives it — and a non-uniform
              scale stretches EVERY glyph in the viewBox with it, so "12m" renders
              wider and thinner as the card grows. The paths are meant to stretch;
              type never is. As HTML the labels also honour the reader's own font
              size, which text baked into a fixed viewBox cannot.

              Positioned by the SAME fraction of the box the plotted point uses, so
              a label cannot drift away from the point it names. */}
          <div style={{ position: 'relative', height: '14px', marginTop: '2px' }} aria-hidden="true">
            {labelledPoints(pts, months, width).map((p) => {
              const first = p.months === sorted[0].months;
              const last = p.months === sorted[sorted.length - 1].months;
              return (
                <span
                  key={p.months}
                  style={{
                    position: 'absolute',
                    left: `${(p.x / width) * 100}%`,
                    // The end labels align to their own edge so neither is clipped
                    // by the card; the ones between are centred on their point.
                    transform: first ? 'none' : last ? 'translateX(-100%)' : 'translateX(-50%)',
                    fontSize: '10.5px',
                    lineHeight: 1.2,
                    color: p.months === months ? 'var(--gps-text)' : 'var(--gps-muted)',
                    fontWeight: p.months === months ? 700 : 400,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.months}m
                </span>
              );
            })}
          </div>
        </div>
      </div>

      {/* THE CURVE IN WORDS. The SVG is aria-hidden, so without this a screen
          reader gets the chosen term's figure and nothing else — the comparison
          between terms, which is the entire reason the band exists, would be
          carried by a picture alone (WCAG 1.4.1). Visually hidden because the
          chart already says it to everyone else. */}
      <ul className="gps-sr-only">
        {sorted.map((p) => (
          <li key={p.months}>
            {p.months} {p.months === 1 ? 'month' : 'months'}: about {label(p)}
            {p.months === months ? ' — the plan length you have chosen' : ''}
          </li>
        ))}
      </ul>

      {/* WHAT THE CURVE ACTUALLY MEANS (2026-08-18, Ashley Haveloch-Jones:
          "frame it so the patient knows, the longer they stay at it, the
          more/better results they'll see").

          Note what this says and what it carefully does NOT. It states the
          CONDITION each figure is attached to — that the month-12 estimate
          describes someone who reached month 12 — which is a fact about how the
          projection is defined, and true whatever the catalogue holds. It does
          not tell the patient how much more they would lose by staying, or when
          progress is fastest, or that later months are better value. Every one of
          those is a quantitative efficacy claim about a prescription-only
          medicine, and the portal has no standing to make it: the figures are
          prices-service's, and any sentence the portal writes about their SHAPE
          would silently become false the day someone seeds a flatter curve. */}
      {/* SHORTER SINCE 2026-09-26 (Ashley Haveloch-Jones, via the CTO: "too much
          text", on a phone above all). Both sentences keep their meaning: each
          point assumes the patient STAYS on treatment to that month (the
          condition above), and the figures are trial averages, not a promise,
          with a clinician's approval still to come. */}
      <p style={{ margin: 'var(--gps-s-4) 0 0', fontSize: '13.5px', lineHeight: 1.5, color: 'var(--gps-text)' }}>
        Each point assumes you <strong>stay on treatment until that month</strong>.
      </p>

      {/* AN ESTIMATE, AND SAID SO PLAINLY rather than in small print. An average
          from trial data is not a forecast for this person, and the clinician
          gate is the real answer to "will I get this". */}
      <p style={{ margin: '8px 0 0', fontSize: '12.5px', color: 'var(--gps-muted)', lineHeight: 1.5 }}>
        {hasWeight ? `From your starting weight of ${formatKg(startKg as number)}. ` : null}
        Based on clinical trial averages — not a promise. Results vary, and a clinician must approve any
        treatment.
      </p>
    </section>
  );
}
